import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Col, Row } from 'reactstrap';
import { Link, useNavigate, useParams } from 'react-router-dom';
import Button from 'components/Button';
import Form from 'components/Form';
import { useForm } from 'react-hook-form';
import TextField from 'components/TextField';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useApiDelete, useApiGet, useApiPost, useApiPut } from 'hooks/useApi';
import useFieldSetErrors from 'hooks/useFieldSetErrors';
import { useToast } from 'hooks/useToast';
import { Loader } from 'react-bootstrap-typeahead';
import Modal from 'components/Modal';
import ToastType from 'enum/ToastType';
import SelectField from 'components/SelectField';
import { AdminExternalNewsChannel } from 'models/AdminExternalNewsChannel';

const schema = yup
  .object({
    name: yup.string().required('O nome é obrigatório.'),
    rssBaseUrl: yup.string().required('A URL base é obrigatória.'),
    isActive: yup.string().oneOf(['true', 'false']).required(),
    template: yup.string().default(''),
    color: yup.string().default(''),
    duration: yup
      .number()
      .typeError('Duração inválida')
      .integer('Duração inválida')
      .min(1, 'Duração inválida')
      .default(15)
  })
  .required();

const AdminNewsChannelsFormPage = () => {
  const params = useParams();
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const navigate = useNavigate();
  const { addToast } = useToast();

  const channelId = params.id;

  const { data, isLoading: isFetching } = useApiGet<AdminExternalNewsChannel>(
    `/admin/news/channels`,
    { id: channelId, enabled: !!channelId }
  );

  const { mutate: mutateCreate, isPending: isCreating } = useApiPost<
    AdminExternalNewsChannel
  >('/admin/news/channels');
  const { mutate: mutateUpdate, isPending: isUpdating } = useApiPut<
    AdminExternalNewsChannel
  >('/admin/news/channels', channelId);
  const { mutate: mutateDelete, isPending: isDeleting } = useApiDelete(
    '/admin/news/channels',
    channelId
  );

  const isSaving = isCreating || isUpdating;
  const mutate = channelId ? mutateUpdate : mutateCreate;

  const formMethods = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      isActive: 'true',
      duration: 15
    }
  });

  const onError = useFieldSetErrors(formMethods.setError);

  useEffect(() => {
    if (!data) return;
    formMethods.reset({
      name: data.name,
      rssBaseUrl: data.rssBaseUrl,
      isActive: String(data.isActive),
      template: data.template || '',
      color: data.color || '',
      duration: data.duration || 15
    } as any);
  }, [data, formMethods]);

  const isActiveOptions = useMemo(
    () => [
      { value: 'true', label: 'Ativo' },
      { value: 'false', label: 'Inativo' }
    ],
    []
  );

  const handleOnSuccess = useCallback(() => {
    addToast(ToastType.SUCCESS, 'Canal salvo com sucesso');
    navigate('/admin/news/channels');
  }, [addToast, navigate]);

  const handleOnSubmit = useCallback(
    (payload: any) => {
      mutate(payload as any, {
        onError,
        onSuccess: handleOnSuccess
      });
    },
    [mutate, onError, handleOnSuccess]
  );

  const handleOnDelete = useCallback(() => {
    if (!channelId) return;
    mutateDelete(null as any, {
      onError: (response: any) => {
        addToast(
          ToastType.ERROR,
          response.errors?.detail?.toString() || 'Erro ao excluir canal'
        );
      },
      onSuccess: () => {
        addToast(ToastType.SUCCESS, 'Canal excluído com sucesso');
        navigate('/admin/news/channels');
      }
    });
  }, [addToast, channelId, mutateDelete, navigate]);

  return (
    <>
      <div className="padding-page">
        <Form onSubmit={handleOnSubmit} {...formMethods}>
          <Row className="d-flex flex-column flex-md-row">
            <Col className="col-12 col-md">
              <div>
                <p className="text-gray-600">
                  <Link
                    to="/admin/news/channels"
                    className="text-decoration-none text-gray-600"
                  >
                    Canais de Notícias
                  </Link>
                  {' / '}
                  {channelId ? 'Editar' : 'Novo'}
                </p>
                <h4 className="fw-bold">
                  {channelId ? 'Editar Canal' : 'Novo Canal'}
                </h4>
                <span className="small-typography text-gray-600">
                  Configure as informações do canal de notícias.
                </span>
              </div>
            </Col>
            <Col className="col-12 col-md align-text mt-3 mt-md-0 d-flex gap-2 justify-content-end">
              {channelId && (
                <Button
                  type="button"
                  color="danger"
                  loading={isDeleting}
                  icon="trash"
                  onClick={() => setShowDeleteConfirmation(true)}
                >
                  Excluir
                </Button>
              )}
              <Button
                color="primary"
                icon="floppy"
                loading={isSaving}
                disabled={!formMethods.formState.isValid}
              >
                Salvar
              </Button>
            </Col>
          </Row>

          {isFetching ? (
            <div className="text-center p-5">
              <Loader />
            </div>
          ) : (
            <div className="bg-white rounded shadow p-4 mt-4">
              <Row>
                <Col md="6">
                  <TextField
                    name="name"
                    label="Nome:"
                    placeholder="Nome do canal"
                  />
                </Col>
                <Col md="6">
                  <TextField
                    name="rssBaseUrl"
                    label="RSS Base URL:"
                    placeholder="https://..."
                  />
                </Col>
              </Row>
              <Row>
                <Col md="3">
                  <SelectField
                    name="isActive"
                    label="Status:"
                    options={isActiveOptions}
                  />
                </Col>
                <Col md="3">
                  <TextField
                    name="template"
                    label="Template:"
                    placeholder="Ex.: TEMPLATE_1"
                  />
                </Col>
                <Col md="3">
                  <TextField name="color" label="Cor:" placeholder="#FFFFFF" />
                </Col>
                <Col md="3">
                  <TextField
                    name="duration"
                    label="Duração (s):"
                    type="number"
                    placeholder="15"
                  />
                </Col>
              </Row>
            </div>
          )}
        </Form>
      </div>

      <Modal
        primaryButtonAction={handleOnDelete}
        primaryButtonLabel="Excluir"
        title="Atenção!"
        isOpen={showDeleteConfirmation}
        showCloseButton={false}
        toggle={() => setShowDeleteConfirmation((x) => !x)}
        secondaryButtonAction={() => setShowDeleteConfirmation(false)}
        secondaryButtonLabel="Cancelar"
        isLoading={isDeleting}
      >
        <p>
          Deseja realmente excluir o canal? <br />
          <small>Essa alteração não poderá ser revertida.</small>
        </p>
      </Modal>
    </>
  );
};

export default AdminNewsChannelsFormPage;
